import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import RepeaterList from '../../components/RepeaterList';
import { visitorNeedsName, type VisitorRow } from './visitorRows';

/**
 * Who visited the excavation that day, as a list of name/function rows.
 *
 * Embedded on the diary doc rather than a collection of its own (see the RxDB
 * collection cap), so the rows are plain state here and the form folds them
 * into its dirty check and its save.
 */
export default function DiaryVisitorsEditor({
    value,
    onChange
}: {
    value: VisitorRow[];
    onChange: (rows: VisitorRow[]) => void;
}) {
    const intl = useIntl();

    return (
        <RepeaterList
            rows={value}
            onChange={onChange}
            blankRow={{ name: '', position: '', note: '' }}
            emptyId="diary.visitor.empty"
            addId="diary.visitor.add"
            removeId="diary.visitor.remove"
            // Each visitor on their own tinted ground: the entries are two rows
            // tall and at the field gap they read as one long grid.
            band
            align="center"
            renderRow={(visitor, update) => (
                // The visitor's own fields: name and function side by side, the
                // note under them. Three in a row leave none of them usable on
                // a tablet, and the note is the one that runs long.
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            label={intl.formatMessage({
                                id: 'diary.visitor.name'
                            })}
                            value={visitor.name}
                            onChange={(event) =>
                                update({ name: event.target.value })
                            }
                            required
                            // Flagged only once the row says something else: a
                            // freshly added row is empty by definition, and
                            // turning it red on sight would scold somebody.
                            error={visitorNeedsName(visitor)}
                            helperText={
                                visitorNeedsName(visitor)
                                    ? intl.formatMessage({
                                          id: 'validation.required'
                                      })
                                    : undefined
                            }
                            // Usually a name, but an ExcaBook import may put a
                            // whole visit log here (its Besuch column is free
                            // text) — grow rather than hide it.
                            multiline
                            maxRows={8}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label={intl.formatMessage({
                                id: 'diary.visitor.position'
                            })}
                            value={visitor.position}
                            onChange={(event) =>
                                update({ position: event.target.value })
                            }
                            sx={{ flex: 1 }}
                        />
                    </Box>
                    <TextField
                        label={intl.formatMessage({ id: 'diary.visitor.note' })}
                        value={visitor.note}
                        onChange={(event) =>
                            update({ note: event.target.value })
                        }
                        // What was discussed, agreed or looked at — the sheets
                        // write a paragraph here as readily as a few words.
                        multiline
                        maxRows={8}
                        fullWidth
                    />
                </Stack>
            )}
        />
    );
}
